/**
 * Hooks para manejar imágenes de usuario en la versión web.
 * Adaptado para usar el servicio userImagesService con Firebase Storage.
 * Integra notificaciones Toast para informar al usuario sobre el estado de las operaciones.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { getUserImages, insertUserImage, softDeleteUserImage, uploadImage, softDeleteImageFromStorage } from '../services/userImagesService';

// Hook para obtener imágenes del usuario en un álbum.
// Recibe album y funciones de toast opcionales (showError).
// Devuelve query con data, isLoading, etc.
export const useUserImagesQuery = (album, toastFunctions = {}) => {
  const { user } = useAuth();
  const { showError } = toastFunctions;

  return useQuery({
    queryKey: ['userImages', user?.id, album],
    queryFn: () => getUserImages(user?.id, album),
    enabled: !!user?.id && !!album,
    staleTime: 0, // Siempre considerar los datos como stale para forzar refresco
    refetchOnWindowFocus: true,
    onError: (error) => {
      // Muestra notificación de error cuando falla la carga de imágenes
      if (showError) {
        showError('Error al cargar las imágenes. Por favor, intenta nuevamente.');
      }
    }
  });
};

// Hook para subir una imagen.
// Recibe album, onSuccess, onError y funciones de toast opcionales (showLoading, showSuccess, showError, removeToast).
// Devuelve mutate, isPending, etc.
export const useUploadImageMutation = (album, toastFunctions = {}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { onSuccess, onError, showLoading, showSuccess, showError, removeToast } = toastFunctions;

  return useMutation({
    mutationFn: async (file) => {
      // Muestra toast de loading al iniciar la subida
      const loadingId = showLoading ? showLoading('Subiendo imagen...') : null;

      try {
        const result = await uploadImage(album, user?.id, file);

        if (result.statusResponse) {
          // Inserta en DB después de upload exitoso
          await insertUserImage(user?.id, result.url, album, result.storageId);
          // Invalida query para refrescar lista
          queryClient.invalidateQueries({ queryKey: ['userImages', user?.id, album] });

          // Remueve el toast de loading y muestra success
          if (loadingId && removeToast) {
            removeToast(loadingId);
          }
          if (showSuccess) {
            showSuccess('Imagen subida exitosamente');
          }
          if (onSuccess) onSuccess();
        }
        // Si statusResponse es false, no hacemos nada aquí porque el error ya fue lanzado en el servicio
      } catch (error) {
        // Remueve el toast de loading y muestra error con el mensaje específico
        if (loadingId && removeToast) {
          removeToast(loadingId);
        }
        if (showError) {
          // Muestra el mensaje de error específico del servicio (ej: "Imagen excede el tamaño máximo")
          // Configura duration: 0 para que el toast no desaparezca automáticamente
          showError(error.message || 'Error al subir la imagen. Por favor, intenta nuevamente.', { duration: 0 });
        }
        if (onError) onError(error);
        throw error;
      }
    },
  });
};

// Hook para eliminar imágenes múltiples.
// Recibe album, onSuccess, onError y funciones de toast opcionales (showLoading, showSuccess, showError, removeToast).
// Devuelve mutate, isPending, etc.
export const useDeleteImagesMutation = (album, toastFunctions = {}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { onSuccess, onError, showLoading, showSuccess, showError, removeToast } = toastFunctions;

  return useMutation({
    mutationFn: async (images) => {
      console.log('[DELETE MUTATION] Iniciando eliminación de', images.length, 'imágenes');
      console.log('[DELETE MUTATION] Imágenes a eliminar:', images.map(img => ({ img_id: img.img_id, storageId: img.storageId, album: img.album })));

      // Muestra toast de loading al iniciar la eliminación
      const loadingId = showLoading ? showLoading(`Eliminando ${images.length} imagen${images.length > 1 ? 'es' : ''}...`) : null;

      try {
        // Soft delete: mueve archivos a carpeta deleted/ y marca como eliminados en DB.
        const deletePromises = images.map(async (image, index) => {
          console.log(`[DELETE MUTATION] Procesando imagen ${index + 1}/${images.length}:`, { img_id: image.img_id, storageId: image.storageId, album: image.album });

          // Mover archivo a carpeta deleted en Firebase Storage
          const storageResult = await softDeleteImageFromStorage(image.storageId, user?.id);

          if (storageResult.success) {
            console.log(`[DELETE MUTATION] Storage delete exitoso para imagen ${index + 1}, marcando en DB`);
            // Marcar como eliminado en Supabase
            await softDeleteUserImage(image.img_id, image.album, image.storageId);
            console.log(`[DELETE MUTATION] DB delete exitoso para imagen ${index + 1}`);
          } else {
            console.error(`[DELETE MUTATION] Error en storage delete para imagen ${index + 1}:`, storageResult.error);
            throw new Error('Error en soft delete del storage');
          }
        });

        await Promise.all(deletePromises);
        console.log('[DELETE MUTATION] Todas las imágenes eliminadas exitosamente');

        // Remueve el toast de loading y muestra success
        if (loadingId && removeToast) {
          removeToast(loadingId);
        }
        if (showSuccess) {
          showSuccess(`${images.length} imagen${images.length > 1 ? 'es' : ''} eliminada${images.length > 1 ? 's' : ''} exitosamente`);
        }
      } catch (error) {
        // Remueve el toast de loading y muestra error
        if (loadingId && removeToast) {
          removeToast(loadingId);
        }
        if (showError) {
          showError('Error al eliminar las imágenes. Por favor, intenta nuevamente.');
        }
        if (onError) onError(error);
        throw error;
      }
    },
    onSuccess: () => {
      // Invalida query para refrescar lista.
      queryClient.invalidateQueries({ queryKey: ['userImages', user?.id, album] });
      if (onSuccess) onSuccess();
    },
  });
};

// Hook para eliminar una imagen individual.
// Recibe album, onSuccess y funciones de toast opcionales (showLoading, showSuccess, showError, removeToast).
// Devuelve mutate, isPending, etc.
export const useDeleteImageMutation = (album, toastFunctions = {}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { onSuccess, showLoading, showSuccess, showError, removeToast } = toastFunctions;

  return useMutation({
    mutationFn: async (image) => {
      // Muestra toast de loading al iniciar la eliminación
      const loadingId = showLoading ? showLoading('Eliminando imagen...') : null;

      try {
        // Soft delete: mueve archivo a carpeta deleted/ y marca como eliminado en DB.
        const storageResult = await softDeleteImageFromStorage(image.storageId, user?.id);

        if (storageResult.success) {
          // Marcar como eliminado en Supabase
          await softDeleteUserImage(image.img_id, image.album, image.storageId);

          // Remueve el toast de loading y muestra success
          if (loadingId && removeToast) {
            removeToast(loadingId);
          }
          if (showSuccess) {
            showSuccess('Imagen eliminada exitosamente');
          }
        } else {
          throw new Error('Error en soft delete del storage');
        }
      } catch (error) {
        // Remueve el toast de loading y muestra error
        if (loadingId && removeToast) {
          removeToast(loadingId);
        }
        if (showError) {
          showError('Error al eliminar la imagen. Por favor, intenta nuevamente.');
        }
        throw error;
      }
    },
    onSuccess: () => {
      // Invalida query.
      queryClient.invalidateQueries({ queryKey: ['userImages', user?.id, album] });
      if (onSuccess) onSuccess();
    },
  });
};
